import React from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { Card } from '../ui';

interface PlatformData {
  platformId: number;
  platformName: string;
  platformDisplayName: string;
  totalEmv: string;
  totalEmvValue: number;
  postCount: number;
}

interface DailyPlatformData {
  date: string;
  platforms: PlatformData[];
}

interface EMVByPlatformChartsProps {
  data: DailyPlatformData[];
  loading?: boolean;
}

// Platform color mapping - matching EMVCharts palette
const PLATFORM_COLORS: { [key: string]: string } = {
  instagram: '#8b5cf6',   // purple (from EMVCharts)
  tiktok: '#f59e0b',      // amber (from EMVCharts)
  twitter: '#3b82f6',     // blue (from EMVCharts)
  x: '#14b8a6',           // teal - distinct color for X/Twitter
  'x/twitter': '#14b8a6', // teal - for "X/Twitter" display name
  youtube: '#ef4444',     // red
  linkedin: '#10b981',    // green (from EMVCharts)
  facebook: '#6366f1',    // indigo (from EMVCharts)
};

const EMVByPlatformCharts: React.FC<EMVByPlatformChartsProps> = ({ data, loading = false }) => {
  // Transform data for EMV chart
  const emvChartData = data.map(day => {
    const entry: any = { date: day.date };
    day.platforms.forEach(platform => {
      entry[platform.platformDisplayName] = platform.totalEmvValue;
    });
    return entry;
  });

  // Transform data for post count chart
  const postCountChartData = data.map(day => {
    const entry: any = { date: day.date };
    day.platforms.forEach(platform => {
      entry[platform.platformDisplayName] = platform.postCount;
    });
    return entry;
  });

  // Get unique platforms
  const platforms = Array.from(
    new Set(data.flatMap(day => day.platforms.map(p => p.platformDisplayName)))
  );

  // Get color for platform
  const getPlatformColor = (platformName: string): string => {
    const key = platformName.toLowerCase();
    return PLATFORM_COLORS[key] || '#64748b'; // default to slate-500 (gray) if not found
  };

  // Format date for display
  const formatDate = (date: string) => {
    const d = new Date(date);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  // Format EMV value with commas for tooltips
  const formatEMV = (value: number) => {
    return `$${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  // Format EMV value for Y-axis (abbreviated for large numbers)
  const formatYAxisEMV = (value: number) => {
    if (value >= 1000000) {
      return `$${(value / 1000000).toFixed(1)}M`;
    } else if (value >= 1000) {
      return `$${(value / 1000).toFixed(1)}K`;
    }
    return `$${value.toFixed(0)}`;
  };

  if (loading) {
    return (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="p-6 bg-gradient-to-br from-white to-green-50/30 border-green-100">
          <div className="flex items-center justify-center h-80">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-4 border-green-500 mx-auto mb-4"></div>
              <p className="text-sm font-medium text-gray-600">Loading EMV data...</p>
            </div>
          </div>
        </Card>
        <Card className="p-6 bg-gradient-to-br from-white to-blue-50/30 border-blue-100">
          <div className="flex items-center justify-center h-80">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-4 border-blue-500 mx-auto mb-4"></div>
              <p className="text-sm font-medium text-gray-600">Loading post data...</p>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="p-6 bg-gradient-to-br from-white to-green-50/30 border-green-100">
          <h3 className="text-lg font-bold text-gray-800 mb-4">EMV by Platform</h3>
          <div className="flex items-center justify-center h-64 bg-white/50 rounded-lg">
            <div className="text-center">
              <svg className="h-16 w-16 text-gray-300 mx-auto mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
              <p className="text-gray-500 font-medium">No data available</p>
            </div>
          </div>
        </Card>
        <Card className="p-6 bg-gradient-to-br from-white to-blue-50/30 border-blue-100">
          <h3 className="text-lg font-bold text-gray-800 mb-4">Posts by Platform</h3>
          <div className="flex items-center justify-center h-64 bg-white/50 rounded-lg">
            <div className="text-center">
              <svg className="h-16 w-16 text-gray-300 mx-auto mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
              <p className="text-gray-500 font-medium">No data available</p>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* EMV by Platform Chart */}
      <Card className="p-6 bg-gradient-to-br from-white to-green-50/30 border-green-100 hover:shadow-xl transition-all duration-300">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-gray-800">EMV by Platform</h3>
          <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center shadow-sm">
            <svg className="h-5 w-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z" />
            </svg>
          </div>
        </div>
        <ResponsiveContainer width="100%" height={240}>
          <BarChart data={emvChartData} margin={{ left: 20, right: 10, top: 10, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey="date"
              tickFormatter={formatDate}
              style={{ fontSize: '12px' }}
            />
            <YAxis
              tickFormatter={formatYAxisEMV}
              style={{ fontSize: '12px' }}
              width={70}
            />
            <Tooltip
              formatter={(value: number) => formatEMV(value)}
              labelFormatter={formatDate}
            />
            <Legend />
            {platforms.map(platform => (
              <Bar
                key={platform}
                dataKey={platform}
                stackId="emv"
                fill={getPlatformColor(platform)}
              />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </Card>

      {/* Post Count by Platform Chart */}
      <Card className="p-6 bg-gradient-to-br from-white to-blue-50/30 border-blue-100 hover:shadow-xl transition-all duration-300">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-gray-800">Posts by Platform</h3>
          <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow-sm">
            <svg className="h-5 w-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
          </div>
        </div>
        <ResponsiveContainer width="100%" height={240}>
          <BarChart data={postCountChartData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey="date"
              tickFormatter={formatDate}
              style={{ fontSize: '12px' }}
            />
            <YAxis
              allowDecimals={false}
              style={{ fontSize: '12px' }}
            />
            <Tooltip
              formatter={(value: number) => `${value} post${value !== 1 ? 's' : ''}`}
              labelFormatter={formatDate}
            />
            <Legend />
            {platforms.map(platform => (
              <Bar
                key={platform}
                dataKey={platform}
                stackId="posts"
                fill={getPlatformColor(platform)}
              />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </Card>
    </div>
  );
};

export default EMVByPlatformCharts;
